<template>
  <section>
    <Divider />

    <!-- Verified Purchases Label -->
    <div 
      v-if="productData.ratings_avg > 0"
      class="flex items-center gap-1.5 mt-4 md:mt-5 lg:mt-6 xl:mt-7 mb-3 md:mb-4"
    >
      <svg class="w-[16px] h-[16px] md:w-[18px] md:h-[18px] text-[#22c55e]" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
      <span class="text-[13px] md:text-[14px] lg:text-[15px] text-[#22c55e] font-medium">
        {{ $t('user.all_ratings_verified_purchases') }}
      </span>
    </div>

    <div
      :class="[
        'flex flex-wrap gap-y-0 gap-x-0',
        productData.ratings_avg > 0 ? '' : 'mt-4 md:mt-5 lg:mt-6 xl:mt-7',
        productData.ratings_avg === 0 ? '' : 'lg:justify-between'
      ]"
    >
      <!-- General Rate -->
      <div
        :class="[
          'w-full',
          productData.ratings_avg > 0
            ? 'sm:max-w-[30rem] lg:basis-[45%] lg:max-w-[48.875rem]'
            : '',
          productData.ratings_avg === 0
            ? 'flex flex-col items-center'
            : ''
        ]"
      >
        <div
          v-if="productData.ratings_avg > 0"
          class="flex flex-wrap justify-between gap-2 items-center w-full mb-3"
        >
          <div
            class="text-[1rem] lg:text-[1.375rem] xl:text-[1.438rem] 2xl:text-[1.48rem] 3xl:text-[1.563rem] font-bold">
            {{ $t('user.general_rating') }}
          </div>
        </div>

        <strong v-if="productData.ratings_avg"
          class="text-[28px] md:text-[32px] lg:text-[35px] font-bold">{{arabizeNumbers(Number(productData.ratings_avg)).toLocaleString()}}</strong>

        <div v-if="productData.ratings_avg > 0" class="flex items-center justify-between gap-1 mt-0.5 mb-3 md:mb-4 lg:mb-5">
          <StarRating :rating="productData.ratings_avg" :maxStars="maxRating" />
          <div class="text-[14px] md:text-[15px] lg:text-[15.5px] xl:text-[15.8px] 3xl:text-[16px] mt-3 text-[#848484]">
            {{ $t('user.ratings_count') }} ({{arabizeNumbers(Number(productData.ratings_count).toLocaleString())}})
          </div>
        </div>

        <div v-if="productData.ratings_avg > 0">
          <RatingPercentages :ratingsList="productData.rating_percentages" :maxStars="maxRating" />
        </div>

        <!-- No ratings message -->
        <div v-if="productData.ratings_avg === 0" class="flex flex-col items-center justify-center py-6 md:py-8 lg:py-10 px-4 mb-4 w-full">
          <div class="text-center">
            <p class="text-[16px] md:text-[18px] lg:text-[20px] text-gray-600 font-medium mb-2">
              {{ $t('general.no_ratings_for_product') }}
            </p>
          </div>
        </div>

        <!-- buttons -->
        <div 
          class="w-full flex flex-wrap gap-5 lg:gap-x-7 xl:gap-x-9 2xl:gap-x-10 items-center" 
          :class="{
            'mt-1 md:mt-4': productData.ratings_avg > 0,
            'justify-center items-center mt-0': productData.ratings_avg === 0
          }"
        >
          <!-- report product -->
          <ReportProduct class="inline-block" :changeReportModalState="changeReportModalState" />

          <!-- rate the product  -->
          <button 
            :disabled="hasGivenFeedback" 
            @click="() => !hasGivenFeedback && changeRatingModalState(true, false)"
            :class="`${hasGivenFeedback ? 'disabled opacity-50 cursor-not-allowed' : 'hover:text-primary/80 transition-all duration-200'} py-2 px-4 text-primary bg-transparent !outline-none border-none max-w-max text-[14px] md:text-[15px] lg:text-[16px] xl:text-[17px] 2xl:text-[17.2px] 3xl:text-[17.8px] flex gap-2 items-center font-normal cursor-pointer rounded-lg ${productData.ratings_avg === 0 ? 'bg-primary/5 hover:bg-primary/10 border border-primary/20 shadow-sm hover:shadow-md hover:scale-105' : ''}`.trim()"
          >
            <RateIcon class="text-[13px] md:text-[15px] lg:text-[15.5px] 3xl:text-[16px]" />
            <span>{{ hasGivenFeedback ? $t('user.already_rated') : $t('user.product_rating') }}</span>
          </button>
        </div>
      </div>

      <!-- comments - feedback -->
      <div
        v-if="productData.ratings_avg > 0"
        class="w-full lg:basis-[50%] flex lg:justify-end"
      >
        <div class="pr-[3px] mb-[3px] w-full mt-2 lg:mt-0 max-h-[600px] overflow-y-auto scrollbar-hide">
          <Feedbacks :hasReachedFullPages="hasReachedFullPages" :loadMoreComments="loadMoreComments"
            :isLoading="isLoadingComments"
            :isDeletingRate="isDeletingRate"
            :isReportingRate="isReportingRate"
            :isLoadingMore="isLoadingMore"
            :items="feedbackItems"
            :onEditRatingOpenModal="onEditRatingOpenModal"
            :confirmDeletingRate="confirmDeletingRate"
            :handleLikeClick="handleLikeClick"
            :typeRate="typeRate"
            :confirmReportingRate="confirmReportingRate" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// Icons
import StarRating from "@/components/generic/StarRating.vue";
import ReportProduct from "@/components/user/singleProduct/ReportProduct.vue";
import RateIcon from "@/assets/shared/icons/Rate.vue";
import RatingPercentages from "../RatingPercentages.vue";
import Divider from "./Divider.vue";
import Feedbacks from "../feedbacks/Feedbacks.vue";
import { roundWithHalf, arabizeNumbers } from "@/helpers";

export default {
  name: "ProductRate",
  data() {
    return {
      maxRating: 5,
    };
  },
  props: {
    commentsData: {
      type: Object,
      required: true,
      default: () => ({})
    },
    isLoadingComments: {
      type: Boolean,
      default: false,
      required: false,
    },
    productData: {
      type: Object,
      required: true,
      default: () => ({})
    },
    loadMoreComments: {
      type: Function,
      required: true
    },
    hasGivenFeedback: {
      type: Boolean,
      default: false,
      required: true,
    },
    changeRatingModalState: {
      type: Function,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isLoadingMore: {
      type: Boolean,
      default: false,
      required: true
    },
    confirmDeletingRate: {
      type: Function,
      required: true
    },
    isDeletingRate: {
      type: Boolean,
      default: false,
      required: true
    },
    isReportingRate: {
      type: Boolean,
      default: false,
      required: true
    },
    confirmReportingRate: {
      type: Function,
      required: true
    },
    changeReportModalState: {
      type: Function,
      required: true,
    },
    updateEditedRate: {
      type: Function,
      required: false
    },
    handleLikeClick: {
      type: Function,
      required: false
    },
    typeRate: {
      type: String,
      required: false
    }
  },
  components: {
    StarRating,
    RatingPercentages,
    Feedbacks,
    Divider,
    ReportProduct,
    RateIcon
  },
  computed:{
    hasReachedFullPages(){
      if(!this.commentsData){
        return true;
      }
      return this.commentsData?.maxPagesReached
    },
    feedbackItems() {
      const { list = [] } = this.commentsData || {};
      return list; // Return full list without slicing
    }
  },
  methods: {
    arabizeNumbers,
    roundWithHalf,
    onEditRatingOpenModal({ item }){
      this.$emit('updateEditedRate', item);
      const isEditMode = true;
      this.changeRatingModalState(true, isEditMode);
    }
  }
};
</script>

<style>
/* Custom scrollbar styling */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;     /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;             /* Chrome, Safari and Opera */
}

/* Show a subtle scrollbar on hover */
.scrollbar-default::-webkit-scrollbar {
  width: 5px;
  display: block;
}

.scrollbar-default::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.scrollbar-default::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.scrollbar-default::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
